import * as React from "react"
import { useCallback, useEffect, useRef } from "react"
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { useLexicalNodeSelection } from "@lexical/react/useLexicalNodeSelection"

import { OPEN_RAW_HTML_DIALOG_COMMAND } from "@/components/lexical/plugins/raw-html"

interface RawHtmlComponentProps {
  html: string
  nodeKey: string
}

export function RawHtmlComponent({ html, nodeKey }: RawHtmlComponentProps) {
  const [editor] = useLexicalComposerContext()
  const [isSelected, , clearSelection] = useLexicalNodeSelection(nodeKey)
  const containerRef = useRef<HTMLDivElement>(null)
  const shadowRootRef = useRef<ShadowRoot | null>(null)

  useEffect(() => {
    if (!containerRef.current) return

    // Create shadow root if it doesn't exist
    if (!shadowRootRef.current) {
      shadowRootRef.current = containerRef.current.attachShadow({ mode: "open" })
    }

    // Update shadow DOM content
    shadowRootRef.current.innerHTML = `
      <style>
        :host {
          display: block;
          /* Reset any inherited styles */
          all: initial;
          /* Allow selection and interaction */
          -webkit-user-select: text;
          user-select: text;
          /* Match typical email line height */
          line-height: 1.4;
          /* Prevent fixed-width signatures from blowing out the container */
          max-width: 100%;
          overflow: hidden;
        }

        /* Constrain fixed-width elements commonly found in email signatures.
           min-width overrides max-width in CSS, so we must reset it too. */
        table, div, span, p, blockquote {
          min-width: 0 !important;
          max-width: 100% !important;
          width: auto !important;
        }

        /* Allow td/th to keep relative widths for internal layout,
           but prevent them from forcing a minimum width */
        td, th {
          min-width: 0 !important;
          max-width: 100% !important;
        }
        
        /* Ensure links are clickable */
        a {
          cursor: pointer;
        }
        
        /* Preserve image display */
        img {
          max-width: 100%;
          height: auto;
        }
        
        /* Adjust line height for better readability */
        * {
          line-height: inherit;
        }

        .raw-html-parent {
          cursor: pointer;
        }

        .raw-html-parent:hover {
          background: var(--color-secondary) !important;
        }
      </style>
      <div class="raw-html-parent">${html}</div>
    `
  }, [html])

  const handleDoubleClick = useCallback(() => {
    editor.dispatchCommand(OPEN_RAW_HTML_DIALOG_COMMAND, { initialHtml: html, nodeKey })
  }, [editor, html, nodeKey])

  const baseCls = "editor-raw-html-block"
  const className = isSelected ? `${baseCls} editor-raw-html-block-selected` : baseCls

  return (
    <div
      ref={containerRef}
      className={className}
      onDoubleClick={() => {
        clearSelection()
        handleDoubleClick()
      }}
    />
  )
}
